// App.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import PageLayout from "./components/PageLayout";
import BookingFlow from "./components/BookingFlow";
import DistanceForm from "./components/DistanceForm";
import VehicleSelection from "./components/VehicleSelection";
import PersonalDetails from "./components/PersonalDetails";
import PaymentMethod from "./components/PaymentMethod";
import Confirmation from "./components/Confirmation";
import BookingSummary from "./components/BookingSummary";
import Login from './admin/auth/Login';
import PrivateRoute from './admin/auth/PrivateRoute';
import AdminLayout from './admin/components/Layout';
import Dashboard from './admin/dashboard/Dashboard';
import Analytics from './admin/dashboard/Analytics';
import AllBookings from './admin/bookings/AllBookings';
import NewBookings from './admin/bookings/NewBookings';
import AssignedBookings from './admin/bookings/AssignedBookings';
import CompletedBookings from './admin/bookings/CompletedBookings';
import CancelledBookings from './admin/bookings/CancelledBookings';
import VehicleList from './admin/vehicles/VehicleList';
import AddEditVehicle from './admin/vehicles/AddEditVehicle';
import VehiclePricing from './admin/vehicles/Pricing';
import CouponList from './admin/coupons/CouponList';
import AddEditCoupon from './admin/coupons/AddEditCoupon';
import UserList from './admin/users/UserList';
import AddEditUser from './admin/users/AddEditUser';
import UserDetails from './admin/users/UserDetails';
import StopFees from './admin/settings/StopFees';
import PriorBooking from './admin/settings/PriorBooking';
import Zones from './admin/settings/Zones';
import Addons from './admin/settings/Addons';
import Events from './admin/settings/Events';
import LateNightCharges from './admin/settings/LateNightCharges';
import PauseDates from './admin/settings/PauseDates';
import OverchargeLocations from './admin/settings/OverchargeLocations';
import DriverList from './admin/drivers/DriverList';
import DriverJobs from './admin/drivers/DriverJobs';
import AddEditDriver from './admin/drivers/AddEditDriver';
import AdminManagement from './admin/settings/AdminManagement';
import UserLogin from './user/Login';
import UserDashboard from './user/Dashboard';
import UserPrivateRoute from './user/PrivateRoute';
import UserRedirectIfAuth from './user/RedirectIfAuth';

// Refresh Confirmation Modal Component
const RefreshConfirmationModal = ({ show, onConfirm, onCancel }) => {
  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        maxWidth: '400px',
        width: '100%'
      }}>
        <h3>Confirm Refresh</h3>
        <p>Are you sure you want to refresh? All your progress will be lost.</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
          <button 
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f0f0f0',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button 
            onClick={onConfirm}
            style={{
              padding: '8px 16px',
              backgroundColor: '#e74c3c',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
};

// Booking Flow with Refresh Protection
const ProtectedBookingFlow = () => {
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Define which routes should trigger the refresh confirmation
  const protectedRoutes = [
    '/booking/vehicle-selection',
    '/booking/return-vehicle-selection',
    '/booking/personal-details',
    '/booking/payment'
  ];

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (protectedRoutes.includes(location.pathname)) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    const handleUnload = () => {
      // Clear form data from localStorage if needed
      if (protectedRoutes.includes(location.pathname)) {
        localStorage.removeItem('bookingData');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, [location.pathname]);

  const handleRefreshConfirm = () => {
    // Clear any stored booking data
    localStorage.removeItem('bookingData');
    // Redirect to home page
    navigate('/booking');
    // Close the modal
    setShowRefreshModal(false);
    // Actually refresh the page
    window.location.reload();
  };

  const handleRefreshCancel = () => {
    setShowRefreshModal(false);
  };

  // Listen for browser refresh attempts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Detect F5 or Ctrl+R
      if ((e.key === 'F5' || (e.ctrlKey && e.key === 'r')) && protectedRoutes.includes(location.pathname)) {
        e.preventDefault();
        setShowRefreshModal(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [location.pathname]);

  return (
    <>
      <BookingFlow />
      <RefreshConfirmationModal 
        show={showRefreshModal} 
        onConfirm={handleRefreshConfirm} 
        onCancel={handleRefreshCancel} 
      />
    </>
  );
};

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/admin/login" element={<Login />} />
        <Route element={<PrivateRoute />}>
          <Route path="/admin/*" element={<AdminLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="bookings" element={<AllBookings />} />
            <Route path="bookings/new" element={<NewBookings />} />
            <Route path="bookings/assigned" element={<AssignedBookings />} />
            <Route path="bookings/completed" element={<CompletedBookings />} />
            <Route path="bookings/cancelled" element={<CancelledBookings />} />
            <Route path="vehicles" element={<VehicleList />} />
            <Route path="vehicles/add" element={<AddEditVehicle />} />
            <Route path="vehicles/edit/:id" element={<AddEditVehicle />} />
            <Route path="coupons" element={<CouponList />} />
            <Route path="coupons/add" element={<AddEditCoupon />} />
            <Route path="coupons/edit/:id" element={<AddEditCoupon />} />
            <Route path="drivers" element={<DriverList />} />
            <Route path="drivers/jobs/:id" element={<DriverJobs />} />
            <Route path="drivers/add" element={<AddEditDriver />} />
            <Route path="drivers/edit/:id" element={<AddEditDriver />} />
            <Route path="users" element={<UserList />} />
            <Route path="users/add" element={<AddEditUser />} />
            <Route path="users/edit/:id" element={<AddEditUser />} />
            <Route path="settings/admin-management" element={<AdminManagement />} />
            <Route path="settings/stop-fees" element={<StopFees />} />
            <Route path="settings/prior-booking" element={<PriorBooking />} />
            <Route path="settings/zones" element={<Zones />} />
            <Route path="settings/addons" element={<Addons />} />
            <Route path="settings/events" element={<Events />} />
            <Route path="settings/late-night-charges" element={<LateNightCharges />} />
            <Route path="settings/pause-dates" element={<PauseDates />} />
            <Route path="settings/overcharge-locations" element={<OverchargeLocations />} />
          </Route>
        </Route>
        <Route path="/user/login" element={<UserLogin />} />
        <Route element={<UserPrivateRoute />}>
          <Route path="/user/dashboard" element={<UserDashboard />} />
        </Route>
        <Route path="/" element={<Navigate to="/booking" replace />} />
        <Route path="/booking/*" element={<ProtectedBookingFlow />}>
          <Route index element={<DistanceForm />} />
          <Route path="vehicle-selection" element={<VehicleSelection />} />
          <Route path="personal-details" element={<PersonalDetails />} />
          <Route path="payment" element={<PaymentMethod />} />
          <Route path="confirmation" element={<Confirmation />} />
          <Route path="return-trip" element={<DistanceForm isReturnBooking />} />
          <Route path="return-vehicle-selection" element={<VehicleSelection isReturnBooking />} />
        </Route>
        <Route path="*" element={<Navigate to="/booking" replace />} />
      </Routes>
    </Router>
  );
}

export default App;